import { ValidationRules, CertificateInfo, FormErrors } from '../types/certificate';

/**
 * 表单验证工具函数
 * 提供统一的验证规则和验证方法
 */

// 验证规则定义
export const validationRules: ValidationRules = {
  orderNumber: [
    {
      required: true,
      message: '订单号不能为空'
    },
    {
      pattern: /^[A-Za-z0-9-_*:]{3,20}$/,
      message: '订单号格式不正确，请输入3-20位字母、数字、连字符、下划线、星号或冒号'
    }
  ],
  batchNumber: [
    {
      required: true,
      message: '批号不能为空'
    },
    {
      pattern: /^[A-Za-z0-9-_*:]{2,15}$/,
      message: '批号格式不正确，请输入2-15位字母、数字、连字符、下划线、星号或冒号'
    }
  ],
  bucketCount: [
    {
      required: true,
      message: '桶数不能为空'
    }
  ],
  productionDate: [
    {
      required: true,
      message: '生产日期不能为空'
    }
  ],
  shipmentDate: [
    {
      required: true,
      message: '发货日期不能为空'
    }
  ],
  testReportDate: [
    {
      required: true,
      message: '检测报告生成日期不能为空'
    }
  ]
};

/**
 * 验证单个字段
 */
export const validateField = (field: string, value: any): string => {
  const rules = validationRules[field];
  if (!rules) return '';

  for (const rule of rules) {
    // 检查必填项
    if (rule.required && (!value || value === '' || value === 0)) {
      return rule.message;
    }

    // 检查正则表达式
    if (rule.pattern && value && !rule.pattern.test(String(value))) {
      return rule.message;
    }
  }

  return '';
};

/**
 * 验证桶数（特殊数字验证）
 */
export const validateBucketCount = (value: number): string => {
  if (!value || value <= 0) {
    return '桶数必须大于0';
  }
  
  if (!Number.isInteger(value)) {
    return '桶数必须为整数';
  }
  
  if (value > 10000) {
    return '桶数不能超过10000';
  }
  
  return '';
};

/**
 * 验证日期逻辑关系
 */
export const validateDateLogic = (data: CertificateInfo): string[] => {
  const errors: string[] = [];
  
  if (!data.productionDate || !data.shipmentDate || !data.testReportDate) {
    return errors;
  }

  const productionDate = new Date(data.productionDate);
  const shipmentDate = new Date(data.shipmentDate);
  const testReportDate = new Date(data.testReportDate);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // 设置为今天结束时间

  // 生产日期不能晚于发货日期
  if (productionDate > shipmentDate) {
    errors.push('生产日期不能晚于发货日期');
  }

  // 发货日期不能是未来日期
  if (shipmentDate > today) {
    errors.push('发货日期不能是未来日期');
  }

  // 检测报告生成日期不能早于生产日期
  if (testReportDate < productionDate) {
    errors.push('检测报告生成日期不能早于生产日期');
  }

  // 检测报告生成日期不能是未来日期
  if (testReportDate > today) {
    errors.push('检测报告生成日期不能是未来日期');
  }

  return errors;
};

/**
 * 验证整个表单
 */
export const validateForm = (data: CertificateInfo): FormErrors => {
  const errors: FormErrors = {};

  // 验证各个字段
  Object.keys(data).forEach((field) => {
    const value = data[field as keyof CertificateInfo];
    
    if (field === 'bucketCount') {
      const error = validateBucketCount(Number(value));
      if (error) errors[field] = error;
    } else {
      const error = validateField(field, value);
      if (error) errors[field] = error;
    }
  });

  // 验证日期逻辑关系
  const dateErrors = validateDateLogic(data);
  if (dateErrors.length > 0) {
    errors._dateLogic = dateErrors.join('；');
  }

  return errors;
};

/**
 * 检查表单是否有效
 */
export const isFormValid = (data: CertificateInfo): boolean => {
  const errors = validateForm(data);
  return Object.keys(errors).length === 0;
};

/**
 * 格式化日期为 YYYY-MM-DD 格式
 */
export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 获取今天的日期字符串
 */
export const getTodayString = (): string => {
  return formatDate(new Date());
};

/**
 * 生成唯一的检测报告ID
 */
export const generateReportId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `RPT-${timestamp}-${randomStr}`.toUpperCase();
};

/**
 * 生成检测报告访问URL
 */
export const generateReportUrl = (reportId: string): string => {
  // 在实际应用中，这应该是真实的域名
  const baseUrl = window.location.origin;
  return `${baseUrl}/report.html?id=${reportId}`;
};

/**
 * 验证订单号格式
 */
export const validateOrderNumber = (value: string): boolean => {
  return /^[A-Za-z0-9-_*:]{3,20}$/.test(value);
};

/**
 * 验证批号格式
 */
export const validateBatchNumber = (value: string): boolean => {
  return /^[A-Za-z0-9-_*:]{2,15}$/.test(value);
};

/**
 * 检测是否为微信浏览器
 */
export const isWechatBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('micromessenger');
};

/**
 * 生成微信友好的下载页面URL
 */
export const generateWechatDownloadUrl = (reportId: string): string => {
  // 在生产环境中，这应该是你的实际域名
  // 在开发环境中，可以使用本地地址进行测试
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://www.zdtl.top'  // 替换为你的实际域名
    : window.location.origin;
    
  return `${baseUrl}/wechat-download.html?id=${reportId}`;
};

/**
 * 根据平台生成合适的二维码内容
 */
export const generateQRCodeContent = (reportId: string): string => {
  // 对于互联网部署，使用微信友好的下载页面
  return generateWechatDownloadUrl(reportId);
};
